import { Form, Head, usePage } from "@inertiajs/react"
import { useState } from "react"

import InputError from "@/components/input-error"
import TextLink from "@/components/text-link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import AuthLayout from "@/layouts/auth-layout"
import { magicLinksPath, signInPath } from "@/routes"

interface Props {
  referral_code?: string
  invitation_token?: string
  invitation_email?: string
  [key: string]: unknown
}

export default function MagicLinkForm() {
  const { referral_code, invitation_token, invitation_email } =
    usePage<Props>().props

  // Default to signup mode if the URL carries signup context (referral code or invitation).
  const [signingUp, setSigningUp] = useState(
    !!referral_code || !!invitation_token,
  )

  return (
    <AuthLayout
      title={
        signingUp ? "Sign up with a magic link" : "Sign in with a magic link"
      }
      description="Enter your email and we'll send you a link — no password needed"
    >
      <Head title="Magic link" />

      <Form
        method="post"
        action={magicLinksPath()}
        disableWhileProcessing
        className="flex flex-col gap-6"
      >
        {({ processing, errors }) => (
          <>
            <div className="grid gap-6">
              {signingUp && (
                <div className="grid gap-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    required
                    autoFocus
                    autoComplete="name"
                    disabled={processing}
                    placeholder="Full name"
                  />
                  <InputError messages={errors.name} />
                </div>
              )}

              <div className="grid gap-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  required
                  autoFocus={!signingUp}
                  autoComplete="email"
                  placeholder="email@example.com"
                  defaultValue={invitation_email ?? ""}
                  readOnly={!!invitation_email}
                />
                <InputError messages={errors.email} />
              </div>

              {signingUp &&
                (invitation_token ? (
                  <input
                    type="hidden"
                    name="invitation_token"
                    value={invitation_token}
                  />
                ) : (
                  <div className="grid gap-2">
                    <Label htmlFor="referral_code">Referral code</Label>
                    <Input
                      id="referral_code"
                      type="text"
                      name="referral_code"
                      required
                      defaultValue={referral_code ?? ""}
                      placeholder="Enter referral code"
                    />
                    <InputError messages={errors.referral_code} />
                  </div>
                ))}

              <Button type="submit" className="mt-2 w-full">
                {processing && <Spinner />}
                {signingUp
                  ? "Email me a sign-up link"
                  : "Email me a sign-in link"}
              </Button>
            </div>

            <div className="text-muted-foreground text-center text-sm">
              {signingUp ? (
                <>
                  Already have an account?{" "}
                  <button
                    type="button"
                    className="text-foreground underline underline-offset-4"
                    onClick={() => setSigningUp(false)}
                  >
                    Sign in instead
                  </button>
                </>
              ) : (
                <>
                  Don&apos;t have an account?{" "}
                  <button
                    type="button"
                    className="text-foreground underline underline-offset-4"
                    onClick={() => setSigningUp(true)}
                  >
                    Sign up instead
                  </button>
                </>
              )}
            </div>

            <div className="text-muted-foreground text-center text-sm">
              <TextLink href={signInPath()}>Use a password instead</TextLink>
            </div>
          </>
        )}
      </Form>
    </AuthLayout>
  )
}
